import React from "react";

// Lightweight, dependency-free renderer for the structured answers the agent
// returns (lead sentence + "- " bullet points under short **bold** headings).
// Supports: **bold**, "- "/"* "/"• " bullets, numbered lists, and blank-line spacing.

// Parse inline **bold** into React nodes.
function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={`${keyPrefix}-b-${i}`}>{m[1]}</strong>;
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
}

const isBullet = (line) => /^\s*[-*•]\s+/.test(line);
const isNumbered = (line) => /^\s*\d+[.)]\s+/.test(line);

export default function ChatMarkdown({ text, className = "" }) {
  if (typeof text !== "string" || !text.trim()) {
    return <span className={className}>{text}</span>;
  }

  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let list = null; // { ordered, items: [] }

  const flushList = () => {
    if (list && list.items.length) {
      const Tag = list.ordered ? "ol" : "ul";
      blocks.push(
        <Tag
          key={`l-${blocks.length}`}
          className={`${list.ordered ? "list-decimal" : "list-disc"} pl-4 my-1 space-y-0.5`}
        >
          {list.items.map((it, i) => (
            <li key={i}>{renderInline(it, `li-${blocks.length}-${i}`)}</li>
          ))}
        </Tag>
      );
    }
    list = null;
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushList();
      return;
    }
    if (isBullet(line)) {
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(line.replace(/^\s*[-*•]\s+/, ""));
    } else if (isNumbered(line)) {
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(line.replace(/^\s*\d+[.)]\s+/, ""));
    } else {
      flushList();
      // A short line that is entirely bold or ends with ":" reads as a heading.
      const headingLike = /^\*\*[^*]+\*\*:?$/.test(line.trim()) || (/:$/.test(line.trim()) && line.trim().length < 48);
      blocks.push(
        <p key={`p-${blocks.length}`} className={headingLike ? "font-semibold mt-1.5" : "my-0.5"}>
          {renderInline(line, `p-${blocks.length}`)}
        </p>
      );
    }
  });
  flushList();

  return <div className={className}>{blocks}</div>;
}
